import { Component, DestroyRef, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { UtilService } from '../../../services/util.service';
import { API, CompanyDTO, CompanyFullDTO, PlayerHardwareDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { NavigationService } from '@ngx-seatback/utils';
import { TranslateService } from '@ngx-translate/core';
import { ImageUploadModel } from 'src/app/services/gui/image/model';
import { MsOptions } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-player-hardware-edit',
  templateUrl: './player-hardware-edit.component.html',
  styleUrls: ['./player-hardware-edit.component.scss'],
  standalone: false,
})
export class PlayerHardwareEditComponent implements OnInit {
  loading: boolean;
  lastRequest: number;
  depictionUrl: string;
  playerHardware: PlayerHardwareDTO;
  playerHardwareId: number;
  manufacturers: CompanyDTO[];
  depictionImageUpload: ImageUploadModel;

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
    OS: new UntypedFormControl('', [Validators.required]),
    Version: new UntypedFormControl('', [Validators.required]),
    Description: new UntypedFormControl(''),
  });

  msCompanyOptions: MsOptions;

  get Name() {
    return this.form.get('Name');
  }

  get OS() {
    return this.form.get('OS');
  }

  get Version() {
    return this.form.get('Version');
  }

  get Description() {
    return this.form.get('Description');
  }

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    private navService: NavigationService,
    public translate: TranslateService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.lastRequest = -1;

    this.depictionUrl = './assets/img/channel-placeholder.png';
    this.depictionImageUpload = new ImageUploadModel();

    this.playerHardwareId = undefined;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.playerHardwareId = +params.playerHardwareID;
      this.refreshFromServer();
    });

    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.playerHardware.name = value;
    });

    this.OS.valueChanges.subscribe(value => {
      this.playerHardware.os = value;
    });

    this.Version.valueChanges.subscribe(value => {
      this.playerHardware.version = value;
    });

    this.Description.valueChanges.subscribe(value => {
      this.playerHardware.description = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.playerHardware.name);
    this.Name.markAsTouched();

    this.OS.setValue(this.playerHardware.os);
    this.OS.markAsTouched();

    this.Version.setValue(this.playerHardware.version);
    this.Version.markAsTouched();

    this.Description.setValue(this.playerHardware.description);
    this.Description.markAsTouched();
  }

  private refreshFromServer() {
    this.loading = true;

    if (!this.playerHardwareId) {
      this.playerHardware = new PlayerHardwareDTO();
      this.loading = false;
    } else {
      this.api
        .ca_md_PlayerHardware_retrieveById(this.playerHardwareId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (apiPlayerHardware: PlayerHardwareDTO) => {
            this.playerHardware = apiPlayerHardware;
            this.updateForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updatePlayerHardware() {
    this.loading = true;
    if (!this.playerHardware.id) {
      if (this.playerHardware.name) {
        this.api
          .ca_md_PlayerHardware_createPlayerHardware(this.playerHardware)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (createdPlayerHardware: PlayerHardwareDTO) => {
              this.playerHardware = createdPlayerHardware;
              this.navService.navigateToPoint(this.util.RoutingID.playerHardware);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.api
        .ca_md_PlayerHardware_editPlayerHardware(this.playerHardware)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (editedPlayerHardware: PlayerHardwareDTO) => {
            this.playerHardware = editedPlayerHardware;
            this.navService.navigateToPoint(this.util.RoutingID.playerHardware);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  update($event: CompanyFullDTO[], type: string) {
    if (type === 'COMPANIES') {
      if ($event && $event.length > 0) {
        this.playerHardware.manufacturer = $event[0];
      } else {
        this.playerHardware.manufacturer = null;
      }
    }
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'COMPANIES') {
      if (options) {
        this.msCompanyOptions = options;
      }
      if (this.msCompanyOptions?.pagination) {
        this.api
          .shared_Company_retrieveAll(
            {
              search: this.util.encodeSearchQueryParam(false, this.msCompanyOptions?.search),
            },
            this.msCompanyOptions?.pagination
          )
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (comps: CompanyFullDTO[]) => {
              this.manufacturers = comps;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }
}
